import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faKey, faSignOutAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import AlertModal from '../components/alertModal/AlertModal';
import { logout as endSession, openProviderSession } from '../services/sessionService';
import CompanyRepository from '../repositories/CompanyRepository';
import { detailsPath } from '../desktop/pages/Companies/companyFormat';
import { useProvider } from './ProviderContext';
import { loadMyData } from './providerData';
import PhotosPanel from './PhotosPanel';
import CompanyDataPanel from './CompanyDataPanel';

function PasswordForm({ onDone }) {
  const [values, setValues] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const set = (key) => (event) => setValues((previous) => ({ ...previous, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (values.next.length < 6) {
      toastr.warning('A senha nova precisa ter ao menos 6 caracteres.');
      return;
    }
    if (values.next !== values.confirm) {
      toastr.warning('A confirmação não bate com a senha nova.');
      return;
    }
    setSaving(true);
    try {
      // A API troca a senha, encerra as outras sessoes e devolve a sessao nova.
      const session = await CompanyRepository.changePassword({ currentPassword: values.current, newPassword: values.next });
      if (session && session.company) openProviderSession(session);
      toastr.success('Senha alterada.', 'As sessões em outros aparelhos foram encerradas.');
      onDone();
    } catch (err) {
      const message = err && err.response && err.response.data && err.response.data.message;
      toastr.error('Não foi possível alterar a senha.', message || 'Tente de novo em instantes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className='pv-form' onSubmit={handleSubmit}>
      <div className='pv-fields'>
        <label className='pv-field pv-field-wide'>
          <span>Senha atual</span>
          <input type='password' autoComplete='current-password' value={values.current} onChange={set('current')} required />
        </label>
        <label className='pv-field'>
          <span>Senha nova</span>
          <input type='password' autoComplete='new-password' value={values.next} onChange={set('next')} required minLength={6} />
        </label>
        <label className='pv-field'>
          <span>Repita a senha nova</span>
          <input type='password' autoComplete='new-password' value={values.confirm} onChange={set('confirm')} required minLength={6} />
        </label>
      </div>
      <div className='pv-panel-actions'>
        <button type='button' className='gs-btn gs-btn-secondary' onClick={onDone} disabled={saving}>Cancelar</button>
        <button type='submit' className='gs-btn gs-btn-primary' disabled={saving}>{saving ? 'Salvando…' : 'Alterar senha'}</button>
      </div>
    </form>
  );
}

// Minha oficina: como a oficina aparece (fotos), dados da empresa e conta.
export default function ShopPage() {
  const { company } = useProvider();
  const [myData, setMyData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  const load = useCallback(async () => {
    if (!company) return;
    setFailed(false);
    try {
      setMyData(await loadMyData(company));
    } catch (err) {
      setFailed(true);
    }
  }, [company]);

  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    await endSession('provider');
    window.location.href = '/provider/login';
  };

  return (
    <div className='gs-stack'>
      <header className='gs-page-header gs-page-header-row'>
        <div>
          <span className='gs-eyebrow'>Minha oficina</span>
          <h1>Como sua oficina aparece</h1>
        </div>
        {company && (
          <Link to={detailsPath(company)} className='gs-text-btn' target='_blank' rel='noopener noreferrer'>
            <FontAwesomeIcon icon={faExternalLinkAlt} aria-hidden='true' />
            Ver página
          </Link>
        )}
      </header>

      {failed && (
        <p className='gs-notice gs-notice-error' role='alert'>
          Não foi possível carregar os dados da oficina.
          <button type='button' className='gs-link' onClick={load}>Tentar de novo</button>
        </p>
      )}

      {!failed && !myData && <div className='gs-skeleton' aria-busy='true' aria-label='Carregando' />}

      {myData && (
        <>
          <PhotosPanel company={company} myData={myData} onSaved={load} />
          <CompanyDataPanel company={company} myData={myData} onSaved={load} />
        </>
      )}

      <section className='pv-panel' aria-labelledby='pv-account-title'>
        <h2 id='pv-account-title' className='pv-panel-title'>Conta</h2>
        {changingPassword
          ? <PasswordForm onDone={() => setChangingPassword(false)} />
          : (
            <div className='pv-account'>
              <button type='button' className='pv-account-row' onClick={() => setChangingPassword(true)}>
                <FontAwesomeIcon icon={faKey} aria-hidden='true' />
                Alterar senha
              </button>
              <button type='button' className='pv-account-row' onClick={() => setConfirmLogout(true)}>
                <FontAwesomeIcon icon={faSignOutAlt} aria-hidden='true' />
                Sair da conta
              </button>
            </div>
          )}
      </section>

      <AlertModal
        show={confirmLogout}
        message='Deseja sair da área da oficina?'
        onHide={() => setConfirmLogout(false)}
        onCancel={() => setConfirmLogout(false)}
        onSubmit={logout}
      />
    </div>
  );
}
